import qs from 'qs';

import axiosInstance from '../protected';

import {
  getDocumentsUrl,
  getExportUrl,
  getReasonDelaysUrl,
  getServiceRequestsUrl,
  getWorkContentsUrl,
} from '../api.config';

import {getFileExt, renameFile, truncFileExt} from '../../shared/helpers/renameFile';
import {IPageable, IPageableDto} from '../../shared/types/pageable.types';
import {
  IServiceRequest,
  IServiceRequestFilters,
  IServiceRequestParams,
  IServiceRequestStatusHistory,
} from '../../shared/types/serviceRequest.types';
import {IContentTreeItem} from '../../shared/types/workContent.types';

export const ServiceRequestService = {
  async getAll(
    attr: {
      pageable?: IPageable;
      spec?: any;
      params?: IServiceRequestParams;
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<IServiceRequest>>(getServiceRequestsUrl(), {
      params: {
        ...attr.pageable,
        ...attr.spec,
        ...attr.params,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async getAllList(
    attr: {
      pageable?: IPageable;
      spec?: any;
      params?: IServiceRequestParams;
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<IServiceRequest>>(getServiceRequestsUrl('/list'), {
      params: {
        ...attr.pageable,
        ...attr.spec,
        ...attr.params,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async getById(id: number) {
    const res = await axiosInstance.post<IPageableDto<IServiceRequest>>(getServiceRequestsUrl(`/findByIds`), [id]);

    return {data: res.data.content[0]};
  },

  async create(data: IServiceRequest, requestId?: string) {
    const path = requestId ? `?requestId=${requestId}` : '';
    const res = await axiosInstance.post<IServiceRequest>(getServiceRequestsUrl(path), data);

    return res;
  },

  async update(data: IServiceRequest) {
    const res = await axiosInstance.put<IServiceRequest>(getServiceRequestsUrl(), data);

    return res;
  },

  async setDescription(id: number, description: string) {
    const res = await axiosInstance.put<IServiceRequest>(
      getServiceRequestsUrl(`/${id}/description`),
      {},
      {
        params: {
          description,
        },
      },
    );

    return res;
  },

  async setWorker(id: number, workerId: number) {
    const res = await axiosInstance.put<any>(
      getServiceRequestsUrl(`/${id}/setWorker`),
      {},
      {
        params: {
          workerId,
        },
      },
    );

    return res;
  },

  async setWorkerAndGroup(id: string, workerId: number, groupId: number) {
    const res = await axiosInstance.put<any>(
      getServiceRequestsUrl(`/${id}/setWorkerAndGroup`),
      {},
      {
        params: {
          workerId,
          groupId,
        },
      },
    );

    return res;
  },

  async clearExecutor(id: number) {
    const res = await axiosInstance.put<any>(getServiceRequestsUrl(`/${id}/clearExecutor`));

    return res;
  },

  async setWorkerByIDs(ids: number[], workerId: number) {
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/setWorkersByIDs`), ids, {
      params: {
        workerId,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async setWorkers(ids: number[], workerIds: (number | string)[]) {
    const queryString = workerIds.map((id) => `workerUid=${id}`).join('&');
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/setWorkers?${queryString}`), ids, {
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });
    return res;
  },

  async setManager(id: number, managerUid: string) {
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/setManager`), [id], {
      params: {
        managerUid,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async setManagerById(ids: string[], managerId: number) {
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/setManager`), ids, {
      params: {
        managerUid: managerId,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async setWorkerAndGroupByIDs(ids: number[], workerId: number, groupId: number) {
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/setWorkerAndGroupByIDs`), ids, {
      params: {
        workerId,
        groupId,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async setWorkerGroupByIDs(ids: number[], groupId: number) {
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/setWorkerGroupByIDs`), ids, {
      params: {
        groupId,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async clearExecutorByIDs(ids: number[]) {
    const res = await axiosInstance.post<{success: boolean}>(getServiceRequestsUrl(`/clearExecutorByIDs`), ids);

    return res;
  },

  async getStatusHistory(id: number) {
    const res = await axiosInstance.get<IServiceRequestStatusHistory[]>(getServiceRequestsUrl(`/${id}/statuses`));

    return res;
  },

  async getContentTree(id: number) {
    const res = await axiosInstance.get<IContentTreeItem[]>(getServiceRequestsUrl(`/${id}/contentTree`));

    return res;
  },

  async getReport() {
    const res = await axiosInstance.get<any[]>(getServiceRequestsUrl(`/report`));

    return res;
  },

  // required role === MANAGER
  // Доступные смены статусов: EXECUTED | DONE -> READY_TO_PROCESS, EXECUTED -> DONE, DONE -> EXECUTED (comment's required)
  // todo: по-хорошему надо переписать аргументы ф-ции на id и объект, в котором более точно проверять на типы в зависимости от статуса
  async setStatus(id: number, statusCode: 'DONE' | 'EXECUTED' | 'READY_TO_PROCESS', comment?: string) {
    const res = await axiosInstance.put<void>(
      getServiceRequestsUrl(`/${id}/status`),
      {},
      {
        params: comment
          ? {
              status: statusCode,
              comment,
            }
          : {
              status: statusCode,
            },
      },
    );

    return res;
  },

  async setWorkData(id: number, field: Record<string, string | number>) {
    const res = await axiosInstance.post<unknown>(
      getServiceRequestsUrl(`/${id}/content/WORK_DATA`),
      JSON.stringify({
        workContentId: field.id,
        text: typeof field.text === 'string' ? field.text.trim() : field.text,
      }),
    );

    return res;
  },

  async setWorksData(
    id: number,
    fields: [
      string,
      {
        id: number;
        value: string;
        type: string;
        mandatory: boolean;
      },
    ][],
  ) {
    const res = await axiosInstance.post<unknown>(
      getWorkContentsUrl(`/${id}/content/WORKS_DATA`),
      JSON.stringify(
        fields.map((item) => ({
          workContentId: item[1].id,
          text: item[1].value.trim(),
        })),
      ),
    );

    return res;
  },

  async setDocuments(
    id: number,
    field: {documents: File[]; workContentId: number; isReplaceFileNameRequired?: boolean},
  ) {
    const formData = new FormData();
    const uniqueFileStamp = (file: File) => Date.now() + file.lastModified + Math.trunc(Math.random() * 10000);

    const newFiles = field.isReplaceFileNameRequired
      ? field.documents.map((file) =>
          renameFile(file, `serviceRequest__${id}_${uniqueFileStamp(file)}.${getFileExt(file)}`),
        )
      : field.documents.map((file) =>
          renameFile(file, `${truncFileExt(file)}_${id}_${uniqueFileStamp(file)}.${getFileExt(file)}`),
        );

    newFiles.forEach((file) => {
      formData.append('docFiles', file);
    });

    const res = await axiosInstance.post<unknown>(getWorkContentsUrl(`/${id}/content/docs`), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        workContentId: field.workContentId,
        docType: 'SERVICE_REQUESTS_PHOTO',
      },
    });

    return res;
  },

  async deleteDocument(srId: number, docId: string) {
    const res = await axiosInstance.delete<unknown>(getDocumentsUrl(`/${docId}`), {
      params: {
        srId,
      },
    });

    return res;
  },

  // Доступно в статусах: CREATED, NOTIFYING, READY_TO_PROCESS, IN_PROGRESS, EXECUTED
  async reject(id: number, reasonRejectionCode: string) {
    const res = await axiosInstance.post<unknown>(
      getServiceRequestsUrl(`/${id}/reject`),
      {},
      {
        params: {
          reasonRejectionCode,
        },
      },
    );

    return res;
  },

  async getDoneReport(beginDate: string, endDate: string, statuses: string[], filter?: IServiceRequestFilters) {
    const res = await axiosInstance.post<{
      id: string;
      fileName: string;
      mimeType: string;
    }>(
      getExportUrl(`/report`),
      {},
      {
        params: {
          beginDate,
          endDate,
          statuses,
          ...(filter && filter),
        },
        paramsSerializer: {
          serialize: (params) => {
            return qs.stringify(params, {arrayFormat: 'repeat'});
          },
        },
      },
    );

    return res;
  },

  async getCurrentStateReport() {
    const res = await axiosInstance.post<{
      id: string;
      fileName: string;
      mimeType: string;
    }>(getExportUrl(`/currentStateReport`));

    return res;
  },

  async getDelayReport() {
    const res = await axiosInstance.post<{
      id: string;
      fileName: string;
      mimeType: string;
    }>(getExportUrl(`/delayReport`));

    return res;
  },

  async getMasterCounterReport() {
    const res = await axiosInstance.post<{
      id: string;
      fileName: string;
      mimeType: string;
    }>(getExportUrl(`/masterCounterReport`));

    return res;
  },

  async getUniversalReportByIDs(id: string) {
    const res = await axiosInstance.get<{
      id: string;
      fileName?: string;
      mimeType?: string;
    }>(getExportUrl(`/report/getByIDs`), {
      params: {
        id,
      },
    });

    return res;
  },

  async getUniversalReportById(id: string) {
    const res = await axiosInstance.get<{
      id: string;
      fileName: string;
      mimeType: string;
    }>(getExportUrl(`/report/${id}`));

    return res;
  },

  async getReasonDelays(id: number) {
    const res = await axiosInstance.get<unknown>(getReasonDelaysUrl(`?srId=${id}`));

    return res;
  },

  async setReasonDelay(
    id: number,
    data: {
      photos: {docFile: File; docFile2?: File};
      date?: string;
      comment?: string;
    },
  ) {
    const formData = new FormData();

    formData.append('docFile', renameFile(data.photos.docFile, `reasonDelay__${id}_${Date.now()}`));

    if (data.photos.docFile2) {
      formData.append('docFile2', renameFile(data.photos.docFile2, `reasonDelay__${id}_${Date.now()}`));
    }

    const res = await axiosInstance.post<unknown>(getReasonDelaysUrl(`/${id}`), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: data.photos.docFile2
        ? {
            date: data.date || '',
            docType: 'SERVICE_REQUESTS_REASON_DELAY_DOC',
            docType2: 'SERVICE_REQUESTS_REASON_DELAY_DOC',
            comment: data.comment || '',
          }
        : {
            date: data.date || '',
            docType: 'SERVICE_REQUESTS_REASON_DELAY_DOC',
            comment: data.comment || '',
          },
    });

    return res;
  },

  async editReasonDelay(
    srId: string | number,
    id: number,
    data: {
      photos: {docFile?: File; docFile2?: File} | null;
      date?: string;
      comment?: string;
    },
  ) {
    const formData = new FormData();

    if (data.photos) {
      if (data.photos?.docFile) formData.append('docFile', data.photos.docFile);
      if (data.photos?.docFile2) formData.append('docFile2', data.photos.docFile2);
    }

    const res = await axiosInstance.put<unknown>(getReasonDelaysUrl(`/${id}`), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        date: data.date || '',
        comment: data.comment || '',
        ...(data.photos?.docFile && {docType: 'SERVICE_REQUESTS_REASON_DELAY_DOC'}),
        ...(data.photos?.docFile2 && {docType2: 'SERVICE_REQUESTS_REASON_DELAY_DOC'}),
      },
    });

    return res;
  },

  async getImages(id: number) {
    const res = await axiosInstance.get<Blob>(getDocumentsUrl(`/images`), {
      responseType: 'blob',
      params: {
        srId: id,
      },
    });

    return res;
  },

  async getServiceRequestsByContractId(contractIds: number[]) {
    return await axiosInstance.post<IServiceRequest[]>(getServiceRequestsUrl('/findByIds'), contractIds);
  },
};
